import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, X, Play, Pause, RotateCcw, Mic, MicOff, AlertCircle, UtensilsCrossed, Star, PartyPopper } from 'lucide-react';
import { speakInstruction } from '../../services/geminiService';

export const CookingMode = ({ recipe, onClose, onComplete, settings }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const recognitionRef = useRef(null);
  const hasCalledComplete = useRef(false);

  const step = recipe.steps[currentStep];

  const playSuccessSound = () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const notes = [523.25, 659.25, 783.99, 1046.50];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.1);
      gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + i * 0.1 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.1 + 0.8);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + i * 0.1 + 1);
    });
  };

  const handleFinish = () => {
    setIsFinished(true);
    playSuccessSound();
    // Call onComplete callback to increment recipes completed count
    if (onComplete && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      onComplete();
    }
  };

  useEffect(() => {
    if (step?.timerDuration) {
      setTimeLeft(step.timerDuration);
      setTimerActive(false);
    } else {
      setTimeLeft(null);
      setTimerActive(false);
    }
    if (timerRef.current) clearInterval(timerRef.current);
  }, [currentStep, step]);

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.error(err);
      }
    };
    requestWakeLock();
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (timerActive && timeLeft !== null && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => (prev !== null && prev > 0 ? prev - 1 : 0));
      }, 1000);
    } else if (timeLeft === 0) {
      setTimerActive(false);
      if ('vibrate' in navigator) navigator.vibrate([500, 200, 500]);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerActive, timeLeft]);

  const toggleTimer = () => setTimerActive(!timerActive);
  const resetTimer = () => {
    setTimerActive(false);
    setTimeLeft(step?.timerDuration || null);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleSpeak = async () => {
    if (isSpeaking) return;
    setIsSpeaking(true);
    try {
      await speakInstruction(step.instruction);
    } catch (e) {
      console.error("TTS failed", e);
    } finally {
      setTimeout(() => setIsSpeaking(false), 2000);
    }
  };

  const next = () => {
    if (currentStep < recipe.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.lang = 'es-ES';
      recognitionRef.current.interimResults = false;
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[event.results.length - 1][0].transcript.toLowerCase();
        if (transcript.includes('siguiente') || transcript.includes('continuar')) next();
        else if (transcript.includes('anterior') || transcript.includes('atrás')) prev();
        else if (transcript.includes('repetir') || transcript.includes('leer')) handleSpeak();
        else if (transcript.includes('parar') || transcript.includes('detener')) setTimerActive(false);
        else if (transcript.includes('iniciar') || transcript.includes('comenzar')) setTimerActive(true);
      };
      recognitionRef.current.onerror = () => setIsListening(false);
    }
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, [currentStep]);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const renderConfetti = () => {
    if (!isFinished) return null;
    const colors = ['#F97316', '#FBBF24', '#10B981', '#3B82F6', '#EC4899'];
    return Array.from({ length: 50 }).map((_, i) => {
      const left = Math.random() * 100;
      const delay = Math.random() * 2;
      const color = colors[Math.floor(Math.random() * colors.length)];
      return (
        <div 
          key={i} 
          className="confetti-particle animate-confetti-fall"
          style={{ 
            left: `${left}vw`, 
            animationDelay: `${delay}s`,
            backgroundColor: color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px'
          }} 
        />
      );
    });
  };

  return (
    <div 
      data-testid="cooking-mode"
      role="dialog" 
      aria-labelledby="cooking-mode-title"
      className={`fixed inset-0 z-50 flex flex-col transition-all duration-500 ${settings?.highContrast ? 'bg-black text-white' : 'bg-stone-50 text-stone-900'}`}
    >
      {renderConfetti()}

      {isFinished && (
        <div className="absolute inset-0 z-[110] flex items-center justify-center bg-base-white/60 dark:bg-base-dark/60 backdrop-blur-xl animate-in fade-in duration-500">
          <div className="bg-white dark:bg-surface-dark p-12 rounded-[56px] shadow-2xl border border-stone-100 dark:border-stone-800 text-center max-w-md mx-6 animate-scale-bounce">
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-8 text-white shadow-xl shadow-primary/20">
              <Star size={48} fill="currentColor" />
            </div>
            <h2 className="text-4xl font-black heading-serif mb-4 dark:text-white">¡Plato Terminado!</h2>
            <p className="text-stone-500 dark:text-stone-400 font-medium mb-10 leading-relaxed text-lg">Has completado la receta con éxito. ¡Buen provecho!</p>
            <button 
              data-testid="close-cooking-mode"
              onClick={onClose}
              className="w-full py-6 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-[32px] font-black uppercase tracking-[0.2em] text-xs shadow-xl transition-all hover:scale-105"
            >
              Cerrar y Compartir
            </button>
          </div>
        </div>
      )}

      <header className="p-6 flex items-center justify-between border-b border-stone-200/50 backdrop-blur-md bg-inherit/80 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <div className="bg-amber-500 p-2 rounded-xl text-white">
            <UtensilsCrossed size={20} />
          </div>
          <h2 id="cooking-mode-title" className={`font-black uppercase tracking-widest text-xs ${settings?.largeText ? 'text-lg' : ''}`}>
            {recipe.title}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={toggleListening}
            aria-label={isListening ? "Desactivar comandos de voz" : "Activar comandos de voz"}
            className={`p-4 rounded-2xl transition-all ${isListening ? 'bg-amber-500 text-white animate-pulse' : 'bg-stone-100 text-stone-400'}`}
          >
            {isListening ? <Mic size={24} /> : <MicOff size={24} />}
          </button>
          <button 
            onClick={onClose}
            aria-label="Cerrar modo cocina"
            className="p-4 rounded-2xl bg-stone-100 text-stone-400 hover:text-rose-500 hover:bg-rose-50 transition-all"
          >
            <X size={24} />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-12 flex flex-col items-center justify-center">
        <div className="max-w-3xl w-full">
          <div className="mb-12 text-center">
            <span className={`inline-block px-6 py-2 rounded-2xl font-black uppercase tracking-[0.2em] text-xs mb-6 ${settings?.highContrast ? 'bg-white text-black' : 'bg-amber-100 text-amber-700'}`}>
              Paso {currentStep + 1} de {recipe.steps.length}
            </span>
            <div className={`w-full bg-stone-200 h-3 rounded-full overflow-hidden shadow-inner`}>
              <div 
                className="bg-amber-500 h-full transition-all duration-700 ease-out" 
                style={{ width: `${((currentStep + 1) / recipe.steps.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="mb-12 text-center min-h-[120px] flex items-center justify-center">
            <p className={`font-bold leading-tight transition-all duration-500 ${settings?.largeText ? 'text-5xl' : 'text-4xl'} ${settings?.highContrast ? 'text-white' : 'text-stone-900'}`}>
              {step?.instruction}
            </p>
          </div>

          {timeLeft !== null && (
            <div className="mb-12 flex flex-col items-center animate-in zoom-in-95 duration-500">
              <div className={`p-8 rounded-[40px] border-4 flex flex-col items-center justify-center shadow-2xl transition-all ${timeLeft === 0 ? 'bg-rose-500 border-rose-600 animate-bounce' : settings?.highContrast ? 'bg-stone-900 border-white/20' : 'bg-white border-stone-100'}`}>
                <div className={`text-6xl font-black mb-6 tabular-nums ${timeLeft === 0 ? 'text-white' : settings?.highContrast ? 'text-white' : 'text-stone-900'}`}>
                  {formatTime(timeLeft)}
                </div>
                <div className="flex gap-4">
                  <button 
                    onClick={toggleTimer}
                    className={`p-5 rounded-[20px] transition-all shadow-lg ${timerActive ? 'bg-amber-500 text-white' : 'bg-stone-900 text-white'}`}
                  >
                    {timerActive ? <Pause size={32} /> : <Play size={32} />}
                  </button>
                  <button 
                    onClick={resetTimer}
                    className="p-5 rounded-[20px] bg-stone-100 text-stone-400 hover:text-stone-900 transition-all shadow-md"
                  >
                    <RotateCcw size={32} />
                  </button>
                </div>
              </div>
              {timeLeft === 0 && (
                <div className="mt-4 flex items-center gap-2 text-rose-500 font-black uppercase tracking-widest text-xs">
                  <AlertCircle size={16} /> ¡Tiempo terminado!
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col items-center gap-6">
            <button 
              data-testid="speak-btn"
              onClick={handleSpeak}
              disabled={isSpeaking}
              aria-label="Escuchar instrucción"
              className={`flex items-center gap-4 px-12 py-6 rounded-[32px] font-black uppercase tracking-widest text-xs shadow-2xl transition-all active:scale-95 ${
                isSpeaking 
                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed' 
                  : settings?.highContrast ? 'bg-white text-black hover:bg-stone-200' : 'bg-stone-900 text-white hover:bg-amber-500'
              }`}
            >
              <Volume2 size={24} className={isSpeaking ? 'animate-pulse' : ''} />
              {isSpeaking ? 'Escuchando...' : 'Leer en voz alta'}
            </button>
            
            {isListening && (
              <p className="text-[10px] font-black uppercase tracking-widest text-amber-500 animate-pulse">
                Comandos de voz activos: "Siguiente", "Atrás", "Leer"
              </p>
            )}
          </div>
        </div>
      </main>

      <footer className="p-8 border-t border-stone-200/50 backdrop-blur-md bg-inherit/80 grid grid-cols-2 gap-6 sticky bottom-0">
        <button 
          data-testid="prev-step-btn"
          onClick={prev}
          disabled={currentStep === 0}
          aria-label="Anterior paso"
          className={`flex items-center justify-center gap-4 py-8 rounded-[32px] font-black uppercase tracking-widest text-xs transition-all active:scale-95 ${
            currentStep === 0 ? 'opacity-20 pointer-events-none' : settings?.highContrast ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
          }`}
        >
          <ChevronLeft size={32} />
          Anterior
        </button>

        {currentStep === recipe.steps.length - 1 ? (
          <button 
            data-testid="finish-cooking-btn"
            onClick={handleFinish}
            aria-label="Finalizar cocinado"
            className="flex items-center justify-center gap-4 py-8 bg-green-500 text-white rounded-[32px] font-black uppercase tracking-widest text-xs hover:bg-green-600 shadow-xl shadow-green-100 transition-all active:scale-95 group"
          >
            <PartyPopper size={32} className="group-hover:rotate-12 transition-transform" />
            ¡Finalizar!
          </button>
        ) : (
          <button 
            data-testid="next-step-btn"
            onClick={next}
            aria-label="Siguiente paso"
            className="flex items-center justify-center gap-4 py-8 bg-amber-500 text-white rounded-[32px] font-black uppercase tracking-widest text-xs hover:bg-amber-600 shadow-xl shadow-amber-100 transition-all active:scale-95"
          >
            Siguiente
            <ChevronRight size={32} />
          </button>
        )}
      </footer>
    </div>
  );
};
